import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, ShoppingBag, Euro, TrendingUp, Copy, Check, 
  ExternalLink, Clock, CheckCircle, Loader2, AlertCircle,
  Share2, MessageCircle, Mail, Gift, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import { Link, useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_NAMES = {
  starter: 'Ambassador',
  pro: 'Influencer',
  premium: 'Elite'
};

const PLAN_COLORS = {
  starter: 'bg-emerald-500',
  pro: 'bg-blue-500',
  premium: 'bg-amber-500'
};

const STATUS_LABELS = {
  pending: { label: 'In attesa', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  pending_payment: { label: 'Pagamento in attesa', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  processing: { label: 'In elaborazione', color: 'bg-blue-100 text-blue-800', icon: Loader2 },
  shipped: { label: 'Spedito', color: 'bg-purple-100 text-purple-800', icon: ExternalLink },
  delivered: { label: 'Consegnato', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  completed: { label: 'Completato', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Annullato', color: 'bg-red-100 text-red-800', icon: AlertCircle }
};

const COMMISSION_STATUS = {
  pending: { label: 'In attesa', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: 'Approvata', color: 'bg-blue-100 text-blue-800' },
  paid: { label: 'Pagata', color: 'bg-green-100 text-green-800' }
};

const AffiliateDashboard = () => {
  const { user, token, loading: authLoading } = useUser();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (user.role !== 'affiliate') {
        toast.error('Accesso riservato agli affiliati');
        navigate('/pricing');
      } else {
        fetchDashboardData();
      }
    }
  }, [user, authLoading, navigate]);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/affiliate/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      if (error.response?.status === 403) {
        toast.error('Non hai accesso a questa dashboard');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (dashboardData?.affiliate?.referral_code) {
      navigator.clipboard.writeText(dashboardData.affiliate.referral_code);
      setCopied(true);
      toast.success('Codice copiato!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareLink = `https://site-refresh-test-1.preview.emergentagent.com?ref=${dashboardData?.affiliate?.referral_code}`;

  const shareVia = (platform) => {
    const message = `Usa il mio codice ${dashboardData?.affiliate?.referral_code} per avere il 10% di sconto su QANTRI! ${shareLink}`;
    const urls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(message)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(message)}`,
      email: `mailto:?subject=Sconto QANTRI per te!&body=${encodeURIComponent(message)}`
    };
    window.open(urls[platform], '_blank');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" />
      </div>
    );
  }

  if (!user || user.role !== 'affiliate') {
    return null;
  }

  const { affiliate, stats, recent_orders } = dashboardData || {};

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="affiliate-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#064E3B] to-[#065F46] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${PLAN_COLORS[affiliate?.plan] || 'bg-gray-500'}`}>
                  {PLAN_NAMES[affiliate?.plan] || 'Partner'}
                </span>
                <span className="text-emerald-200 text-sm">{affiliate?.commission_rate}% commissione</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold">
                Ciao, {affiliate?.name?.split(' ')[0]}!
              </h1>
              <p className="text-emerald-200 mt-1">Dashboard Affiliato</p>
            </div>
            
            {/* Referral Code Quick Access */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-sm text-emerald-200 mb-2">Il tuo codice referral</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold tracking-wider">{affiliate?.referral_code}</span>
                <Button
                  onClick={copyCode}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-5 shadow-sm"
          >
            <div className="flex items-center justify-between mb-3">
              <Users className="h-5 w-5 text-[#064E3B]" />
              <span className="text-xs text-neutral-500">Referral</span>
            </div>
            <p className="text-2xl font-bold">{stats?.total_referrals || 0}</p>
            <p className="text-sm text-neutral-500">Persone invitate</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-5 shadow-sm"
          >
            <div className="flex items-center justify-between mb-3">
              <ShoppingBag className="h-5 w-5 text-[#064E3B]" />
              <span className="text-xs text-neutral-500">Ordini</span>
            </div>
            <p className="text-2xl font-bold">{stats?.total_orders || 0}</p>
            <p className="text-sm text-neutral-500">Ordini generati</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-5 shadow-sm"
          >
            <div className="flex items-center justify-between mb-3">
              <Euro className="h-5 w-5 text-emerald-500" />
              <span className="text-xs text-neutral-500">Guadagnato</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600">€{stats?.total_commission_earned?.toFixed(2) || '0.00'}</p>
            <p className="text-sm text-neutral-500">Commissioni totali</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-5 shadow-sm"
          >
            <div className="flex items-center justify-between mb-3">
              <Clock className="h-5 w-5 text-amber-500" />
              <span className="text-xs text-neutral-500">In attesa</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">€{stats?.pending_commission?.toFixed(2) || '0.00'}</p>
            <p className="text-sm text-neutral-500">Da approvare</p>
          </motion.div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Share & Quick Actions */}
          <div className="lg:col-span-1 space-y-6">
            {/* Share Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl p-6 shadow-sm"
            >
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Share2 className="h-5 w-5 text-[#064E3B]" />
                Condividi e Guadagna
              </h3>
              
              <p className="text-sm text-neutral-600 mb-4">
                Ogni volta che qualcuno usa il tuo codice, guadagni il <strong>{affiliate?.commission_rate}%</strong> della vendita!
              </p>

              <div className="space-y-2">
                <Button
                  onClick={() => shareVia('whatsapp')}
                  className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white justify-start"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Condividi su WhatsApp
                </Button>
                <Button
                  onClick={() => shareVia('telegram')}
                  className="w-full bg-[#0088cc] hover:bg-[#0077b5] text-white justify-start"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Condividi su Telegram
                </Button>
                <Button
                  onClick={() => shareVia('email')}
                  variant="outline"
                  className="w-full justify-start"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Invia via Email
                </Button>
              </div>
            </motion.div>

            {/* Referral Credits */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <Gift className="h-6 w-6 text-amber-600" />
                <h3 className="font-semibold">Crediti Bonus</h3>
              </div>
              <p className="text-3xl font-bold text-amber-700">€{stats?.referral_credits || 0}</p>
              <p className="text-sm text-amber-600 mt-1">
                Guadagni €5 per ogni nuovo utente registrato con il tuo codice
              </p>
            </motion.div>

            {/* Upgrade CTA (for non-premium users) */}
            {affiliate?.plan !== 'premium' && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-[#064E3B] to-[#065F46] rounded-xl p-6 text-white"
              >
                <h3 className="font-semibold mb-2">Vuoi guadagnare di più?</h3>
                <p className="text-sm text-emerald-200 mb-4">
                  Passa a un piano superiore e aumenta le tue commissioni fino al 15%!
                </p>
                <Link to="/pricing">
                  <Button className="w-full bg-white text-[#064E3B] hover:bg-neutral-100">
                    Scopri i Piani
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </motion.div>
            )}
          </div>

          {/* Right Column - Recent Orders */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2 bg-white rounded-xl shadow-sm"
          >
            <div className="p-6 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#064E3B]" />
                Ordini Recenti
              </h3>
              <p className="text-sm text-neutral-500">Gli ordini generati con il tuo codice referral</p>
            </div>

            {recent_orders && recent_orders.length > 0 ? (
              <div className="divide-y">
                {recent_orders.map((order, index) => {
                  const orderStatus = STATUS_LABELS[order.status] || STATUS_LABELS.pending;
                  const commissionStatus = COMMISSION_STATUS[order.commission_status] || COMMISSION_STATUS.pending;
                  
                  return (
                    <motion.div
                      key={order.order_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 hover:bg-neutral-50 transition-colors"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">#{order.order_id.slice(-8)}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${orderStatus.color}`}>
                              {orderStatus.label}
                            </span>
                          </div>
                          <p className="text-xs text-neutral-500">
                            {new Date(order.date).toLocaleDateString('it-IT', { 
                              day: 'numeric', 
                              month: 'short', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-neutral-500">Ordine: €{order.order_total?.toFixed(2)}</p>
                            <p className="font-semibold text-emerald-600">
                              +€{order.commission_amount?.toFixed(2)}
                              <span className="text-xs text-neutral-400 ml-1">({order.commission_percent}%)</span>
                            </p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${commissionStatus.color}`}>
                            {commissionStatus.label}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="p-12 text-center">
                <ShoppingBag className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                <h4 className="font-medium text-neutral-600 mb-2">Nessun ordine ancora</h4>
                <p className="text-sm text-neutral-500 mb-4">
                  Condividi il tuo codice referral per iniziare a guadagnare commissioni!
                </p>
                <Button onClick={() => shareVia('whatsapp')} className="bg-[#064E3B] hover:bg-[#053d2f]">
                  <Share2 className="h-4 w-4 mr-2" />
                  Condividi Ora
                </Button>
              </div>
            )}
          </motion.div>
        </div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 bg-white rounded-xl p-8 shadow-sm"
        >
          <h3 className="text-xl font-semibold mb-6 text-center">Come funziona il programma</h3>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-[#064E3B] text-white rounded-full flex items-center justify-center mx-auto mb-3 text-lg font-bold">
                1
              </div>
              <h4 className="font-medium mb-1">Condividi</h4>
              <p className="text-sm text-neutral-500">Invia il tuo codice ad amici e follower</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#064E3B] text-white rounded-full flex items-center justify-center mx-auto mb-3 text-lg font-bold">
                2
              </div>
              <h4 className="font-medium mb-1">Loro risparmiano</h4>
              <p className="text-sm text-neutral-500">Ricevono il 10% di sconto sul primo ordine</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#064E3B] text-white rounded-full flex items-center justify-center mx-auto mb-3 text-lg font-bold">
                3
              </div>
              <h4 className="font-medium mb-1">Tu guadagni</h4>
              <p className="text-sm text-neutral-500">{affiliate?.commission_rate}% di commissione su ogni vendita</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center mx-auto mb-3 text-lg font-bold">
                4
              </div>
              <h4 className="font-medium mb-1">Ricevi il pagamento</h4>
              <p className="text-sm text-neutral-500">Via PayPal ogni mese (min. €20)</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AffiliateDashboard;
